import { UserRole } from '@kfolks/types'

/**
 * Declarative navigation config for Flight Control.
 *
 * Filtered at render time by `filterNav(config, viewer)`.
 *
 * Gating conventions (ADR-0008):
 *
 * 1. Every group MUST declare `visibleFor` explicitly. No implicit "open to all"
 *    groups. This keeps the access intent obvious at a glance.
 * 2. Item-level `minRole` is the per-item ordinal floor
 *    (VIEWER < USER < ADMIN < SUPER_ADMIN).
 *    It composes AND-wise with the group's `visibleFor`: an item renders only
 *    when both pass.
 * 3. `visibleIf` is a closed-set string predicate resolved by `filterNav`.
 *    Supported predicates:
 *      - `has-ever-had-pending-approval-or-admin` — visible if the viewer is
 *        admin OR has ever been assigned as an approver on any request.
 *
 * Visibility matrix (per product decision, see ADR-0008 + ADR-0009):
 *
 *   | Group                 | USER | ADMIN | SUPER_ADMIN | VIEWER |
 *   |-----------------------|------|-------|-------------|--------|
 *   | personal              | yes  | yes   | yes         | no     |
 *   | people                | no   | no    | yes         | no     |
 *   | organization          | no   | no    | yes         | no     |
 *   | access-security       | no   | no    | yes         | no     |
 *   | configuration         | no   | no    | yes         | no     |
 *   | request-center-admin  | no   | yes   | yes         | no     |
 *
 * Rationale: ADMIN is scoped to Request Center only (operates the request
 * flow). SUPER_ADMIN operates the rest of the platform (people, organization,
 * access/security, configuration). USERs do one thing — submit and track
 * their own requests. Backend endpoints remain the authoritative access
 * boundary; the sidebar is UX.
 */

const ADMIN_ROLES: UserRole[] = [UserRole.ADMIN, UserRole.SUPER_ADMIN]
const SUPER_ADMIN_ROLES: UserRole[] = [UserRole.SUPER_ADMIN]
const USER_ROLES: UserRole[] = [UserRole.USER, UserRole.ADMIN, UserRole.SUPER_ADMIN]

export interface NavigationItem {
	label: string
	route: string
	minRole?: UserRole
	visibleFor?: UserRole[]
	visibleIf?: 'has-ever-had-pending-approval-or-admin'
}

export interface NavigationGroup {
	group: string
	/** Optional display label for the section header. Falls back to `group` when omitted. */
	label?: string
	visibleFor: UserRole[]
	items: NavigationItem[]
}

export const navigationConfig: NavigationGroup[] = [
	{
		group: 'personal',
		visibleFor: USER_ROLES,
		items: [
			{ label: 'Dashboard', route: '/dashboard', minRole: UserRole.USER },
			{ label: 'My Requests', route: '/requests-center/my', minRole: UserRole.USER },
			{ label: 'Request Catalog', route: '/requests-center/catalog', minRole: UserRole.USER },
			{
				label: 'Approvals',
				route: '/requests-center/approvals',
				minRole: UserRole.USER,
				visibleIf: 'has-ever-had-pending-approval-or-admin',
			},
		],
	},
	{
		group: 'people',
		label: 'People',
		visibleFor: SUPER_ADMIN_ROLES,
		items: [
			{ label: 'Employees', route: '/employees' },
			{ label: 'Contractors', route: '/contractors' },
			{ label: 'Entra Directory', route: '/entra-directory' },
		],
	},
	{
		group: 'organization',
		label: 'Organization',
		visibleFor: SUPER_ADMIN_ROLES,
		items: [
			{ label: 'Clients', route: '/clients' },
			{ label: 'Projects', route: '/projects' },
			{ label: 'Providers', route: '/providers' },
			{ label: 'Files', route: '/files' },
		],
	},
	{
		group: 'access-security',
		label: 'Access & Security',
		visibleFor: SUPER_ADMIN_ROLES,
		items: [
			{ label: 'IDM Requests', route: '/idm-requests' },
			{ label: 'ACC Mappings', route: '/idm-acc-mappings' },
			{ label: 'Permissions', route: '/permissions' },
			{ label: 'Users', route: '/users' },
		],
	},
	{
		group: 'configuration',
		label: 'Configuration',
		visibleFor: SUPER_ADMIN_ROLES,
		items: [
			{ label: 'Master Data', route: '/master-data' },
			{ label: 'Settings', route: '/settings' },
		],
	},
	{
		group: 'request-center-admin',
		label: 'Request Center Admin',
		visibleFor: ADMIN_ROLES,
		items: [
			{ label: 'Request Types', route: '/admin/request-center/types' },
			{ label: 'Data Sources', route: '/admin/request-center/data-sources' },
			{ label: 'Systems', route: '/admin/request-center/systems' },
			{ label: 'Tickets', route: '/admin/request-center/tickets' },
			{ label: 'Audit Search', route: '/admin/request-center/audit' },
		],
	},
]
